/**
 * clientErrorBuffer — Lightweight sessionStorage-backed ring of recent
 * client-side errors, used to enrich bug reports with context that would
 * otherwise be lost (uncaught async errors, ErrorBoundary crashes).
 *
 * Design goals:
 *  - Zero new infrastructure. No new DB table, no new network call.
 *  - Single session only (sessionStorage, not localStorage) so errors from
 *    a previous tab/user don't bleed into the current user's bug report.
 *  - Bounded size (BUFFER_LIMIT) so we can't blow out storage quotas.
 *  - Safe under SSR / embedded contexts where sessionStorage may throw.
 *
 * Usage:
 *   pushClientError({ source: 'error-boundary', message, stack, route })
 *   const recent = readRecentClientErrors()
 *   clearClientErrors() // called after successful bug report submission
 */

const BUFFER_KEY = 'optibay:client-error-buffer';
const BUFFER_LIMIT = 20;

/**
 * PRIVACY CONTRACT (R43): the buffer flows into `bug_reports.context_json`
 * when the user files a report. Stack traces and the `extra` field
 * occasionally contain PII fragments (e.g., a customer name embedded in a
 * SQL error string, a URL with a query param). Callers SHOULD avoid
 * placing raw customer/vehicle PII into `extra` when other identifiers
 * (ticket id, shop id, ids only) suffice.
 *  - `message` and `stack` are accepted as-is (truncating would break
 *    debugging utility); we trust JS runtime + library messages not to
 *    embed PII.
 *  - `extra` is the explicit caller-controlled escape hatch — keep it
 *    minimal. Prefer `ticketId`, `shopId`, opcode, etc. over names.
 *  - The buffer never leaves the user's session storage until they file
 *    a bug report (explicit consent), so worst-case exposure is to OptiBay
 *    support staff who already have RLS-gated access to the same data.
 */
export interface ClientErrorRecord {
  timestamp: string;
  source:
    | 'error-boundary'
    | 'window-error'
    | 'unhandled-rejection'
    | 'realtime'
    | 'mutation'
    | 'other';
  message: string;
  stack?: string;
  route?: string;
  userId?: string | null;
  shopId?: string | null;
  role?: string | null;
  supportSessionId?: string | null;
  /** Caller-controlled context. Prefer ids over PII strings — see PRIVACY CONTRACT above. */
  extra?: Record<string, unknown>;
}

function safeGetBuffer(): ClientErrorRecord[] {
  try {
    const raw = sessionStorage.getItem(BUFFER_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function safeWriteBuffer(entries: ClientErrorRecord[]): void {
  try {
    sessionStorage.setItem(BUFFER_KEY, JSON.stringify(entries));
  } catch {
    // quota exceeded / unavailable — drop silently
  }
}

export function pushClientError(record: Omit<ClientErrorRecord, 'timestamp'>): void {
  try {
    const entry: ClientErrorRecord = {
      timestamp: new Date().toISOString(),
      ...record,
    };
    const existing = safeGetBuffer();
    existing.push(entry);
    // Keep only the most recent BUFFER_LIMIT entries.
    const trimmed = existing.slice(-BUFFER_LIMIT);
    safeWriteBuffer(trimmed);
  } catch {
    // last-ditch: never throw out of a logger
  }
}

export function readRecentClientErrors(): ClientErrorRecord[] {
  return safeGetBuffer();
}

export function clearClientErrors(): void {
  try {
    sessionStorage.removeItem(BUFFER_KEY);
  } catch {
    // ignore
  }
}
